import { AuthApiS, AuthSchemaS, type AuthTypeS, AuthValidationS } from '@/features/Auth/outbound/share';
import {
  CertificationApiS,
  CertificationConstantS,
  CertificationSchemaS,
  CertificationValidationS,
} from '@/features/Certification/outbound/share';
import { MCO_DOMAIN, MCO_MARKETING_PATH, MCO_PRIVACY_PATH, MCO_TERMS_PATH } from '@/libs/constant';
import type { ErrorJson } from '@/libs/errors';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { AT, T } from '@/libs/translation';
import { makeResultUrl } from '@/libs/util/makeServiceUrl';
import { replaceBrToSpace } from '@/libs/util/replaceBrToSpace';
import { isValidEmail } from '@/libs/validation/email';
import { checkOnlyValidNicknameChar, checkValidWordNicknameChar } from '@/libs/validation/nickname';
import { checkConsecutiveChar, isValidPassword } from '@/libs/validation/password';
import {
  Button,
  Checkbox,
  Input,
  InputChangeEvent,
  InputTimerEndEvent,
  type InputValidatorOption,
  Link,
  Typo,
} from '@/ui/component/atom';
import { Header, Layout, Modal, Toast } from '@/ui/component/cell';
import { AutoCompleteEmailView, EmailDuplicatedView, LogoView, makeErrorPopup, makePopup } from '@/ui/component/view';
import { debounce, getSharedData, PostMessage } from '@/ui/util';
import { each, filter, fromEntries, identity, map, pipe, reject, toArray } from '@fxts/core';
import { isAxiosError } from 'axios';
import { $, type Html, html, Page, View } from 'rune-ts';
import auth_klass from '../Common/Auth.module.scss';
import klass from './SignupEmailPage.module.scss';

export interface SignupEmailPageData {}

export class SignupEmailPageBodyView extends View<SignupEmailPageData> {
  requested_code = false;
  valid_code = false;
  component: {
    email: Input;
    code: Input;
    password: Input;
    nickname: Input;
    request_code: Button;
    submit: Button;
    all_agree: Checkbox;
  };

  constructor(data: SignupEmailPageData) {
    super(data);

    this.component = {
      email: new Input(
        {},
        {
          attr: {
            name: 'email',
            type: 'email',
            inputmode: 'email',
            placeholder: T('auth.login.email_placeholder'),
            required: true,
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('auth.signup.email_invalid_guide'),
                validator: (email) => isValidEmail(email),
              },
            ],
          },
        },
      ),
      code: new Input(
        {},
        {
          attr: {
            name: 'code',
            type: 'text',
            placeholder: T('auth.signup.code_placeholder'),
            maxlength: 6,
            required: true,
            inputmode: 'numeric',
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('auth.signup.code_time_end'),
              },
            ],
          },
          controller: {
            type: 'timer',
            timer: {
              minutes: 8,
              autoplay: false,
            },
          },
          klass: `${klass.code} ${klass.disabled}`,
          delete_pattern: /\D/g,
        },
      ),
      password: new Input(
        {},
        {
          attr: {
            name: 'password',
            type: 'password',
            placeholder: T('auth.login.password_placeholder'),
            required: true,
            autocomplete: 'new-password',
            maxlength: 30,
          },
          helper: {
            validators: this.passwordValidOption(),
          },
        },
      ),
      nickname: new Input(
        {},
        {
          delete_pattern: /\s+/g,
          attr: { name: 'nickname', type: 'text', placeholder: T('auth.signup.nickname_placeholder'), maxlength: 10 },
          helper: {
            validators: this.nicknameValidOption(),
          },
        },
      ),
      request_code: new Button(
        {
          label: new Typo({ text: T('auth.signup.request_email_verify_code') }, { typo: 'p2', weight: 'bold' }),
        },
        {
          size: 'small',
          disabled: true,
          type: 'button',
          klass: `${klass.button} request_code`,
        },
      ),
      all_agree: new Checkbox({
        label: new Typo({ text: T('auth.signup.agree_all') }, { typo: 'p1', weight: 'bold' }),
        name: 'all_agree',
      }),
      submit: new Button(
        {
          label: new Typo({ text: T('auth.signup.complete') }, { weight: 'bold' }),
        },
        {
          disabled: true,
          size: 'large',
        },
      ),
    };
  }

  override template() {
    const { theme, device } = getSharedData(this);

    return html` <div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <form class="${klass.signup} signup">
            <div class="${klass.input_container}">
              <div class="${klass.input_wrapper}">
                ${new AutoCompleteEmailView({}, this.component.email)} ${this.component.request_code}
              </div>
              ${this.component.code} ${this.component.password} ${this.component.nickname}
            </div>
            <div class="${klass.agreement_container}">
              <div class="${klass.all}">${this.component.all_agree}</div>
              <div class="${klass.item_container}">
                ${AT.language === 'kr'
                  ? html`
                      <div class="${klass.item}">
                        ${new Checkbox({
                          label: new Typo({ text: T('auth.signup.agree_age_terms') }),
                          name: 'age_agree',
                        })}
                      </div>
                    `
                  : ''}
                <div class="${klass.item}">
                  ${new Checkbox({
                    label: new Typo({ text: T('auth.signup.agree_usage_terms') }),
                    name: 'usage_agree',
                  })}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_TERMS_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>
                <div class="${klass.item}">
                  ${new Checkbox({
                    label: new Typo({ text: T('auth.signup.agree_privacy_terms') }),
                    name: 'privacy_agree',
                  })}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_PRIVACY_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>

                <div class="${klass.item}">
                  ${new Checkbox({
                    label: new Typo({ text: T('auth.signup.agree_marketing_terms') }),
                    name: 'is_agree_notification',
                  })}
                  ${new Link(
                    {
                      label: new Typo({ text: T('common.see') }, { underline: true, color: 'gray_50' }),
                      href: MCO_MARKETING_PATH,
                    },
                    {
                      target: '_blank',
                      klass: `see_more ${klass.see_more}`,
                    },
                  )}
                </div>
              </div>
            </div>
            <div class="${klass.button_container}">
              ${device === 'mo'
                ? ''
                : new Button(
                    {
                      label: new Typo({ text: T('common.back') }, { weight: 'bold' }),
                    },
                    {
                      size: 'large',
                      type: 'button',
                      klass: `${klass.button} back`,
                      color: 'line',
                    },
                  )}
              ${this.component.submit}
            </div>
          </form>
        </div>
      </div>
    </div>`;
  }

  passwordValidOption(): InputValidatorOption[] {
    return [
      {
        message: T('auth.signup.password_valid_1'),
        validator: isValidPassword,
      },
      {
        message: T('auth.signup.password_valid_2'),
        validator: checkConsecutiveChar,
      },
      {
        message: T('auth.signup.password_valid_3'),
        validator: (value) => {
          return this.component.email.value
            ? AuthValidationS.checkEmailChar(value, this.component.email.value ?? '')
            : true;
        },
      },
    ];
  }

  nicknameValidOption(): InputValidatorOption[] {
    return [
      {
        message: T('auth.signup.nickname_valid_1'),
        validator: (val) => val.length >= 2 && val.length <= 10,
      },
      {
        message: T('auth.signup.nickname_valid_2'),
        validator: checkOnlyValidNicknameChar,
      },
      {
        message: T('auth.signup.nickname_valid_3'),
        validator: checkValidWordNicknameChar,
      },
    ];
  }

  override onRender() {
    this.component.email.focus();

    this.delegate('click', '.back', () => {
      history.back();
    });
    this.delegate(InputTimerEndEvent, Input, this.timerEndHandler);
    const changeHandler = debounce(this.formChangeHandler.bind(this), 100);
    this.delegate(InputChangeEvent, Input, changeHandler);
    this.delegate(InputChangeEvent, Input, (e, input) => {
      if (input.option.attr?.name === 'email') {
        this.component.password.resetValidator();
      }
    });
    this.delegate('input', '.signup', changeHandler);
    this.delegate('submit', '.signup', this.formSubmitHandler);
    this.delegate('click', '.request_code', this.requestCodeHandler);
    this.delegate('change', Checkbox, this.chechBoxChangeHandler);
    this.delegate('click', '.see_more', this.linkClickHandler);
  }

  isCheckAgree(reject_keys: string[]): boolean {
    const rest_checkboxes = pipe(
      this.subViews(Checkbox),
      reject((checkbox) => reject_keys.includes(checkbox.data.name)),
      toArray,
    );

    return pipe(
      rest_checkboxes,
      map((checkbox) => checkbox.isChecked()),
      filter(identity),
      toArray,
      (check_list) => check_list.length === rest_checkboxes.length,
    );
  }

  getForm() {
    return $(this.element()).find('.signup')?.element() as HTMLFormElement;
  }
  getAgreeData() {
    return pipe(
      this.subViews(Checkbox),
      map((checkbox): [string, boolean | undefined] => [checkbox.data.name, checkbox.isChecked()]),
      fromEntries,
    );
  }

  async timerEndHandler() {
    const res = await makePopup({
      title: T('auth.signup.code_timer_done'),
      message: T('auth.signup.code_timer_done_description'),
      ok: T('auth.signup.code_retry'),
      cancel: T('common.cancel'),
    });

    if (res === 'confirm') {
      return this.requestCodeHandler();
    }
  }
  linkClickHandler(e: Event & { originalEvent: Event }) {
    const { app_info } = getSharedData(this);

    if (app_info?.name === 'MarppleShop') {
      e.originalEvent.preventDefault();
      PostMessage.goEmptyScreen({ uri: (e.currentTarget as HTMLAnchorElement).href });
    }
  }
  chechBoxChangeHandler(_e: Event, view: Checkbox) {
    if (view.data.name === this.component.all_agree.data.name) {
      each((checkbox) => (view.isChecked() ? checkbox.activate() : checkbox.deactivate()), this.subViews(Checkbox));
    } else {
      this.isCheckAgree(['all_agree']) ? this.component.all_agree.activate() : this.component.all_agree.deactivate();
    }
  }
  async requestCodeHandler() {
    if (this.valid_code) return;

    try {
      await pipe(
        { email: this.component.email.value, certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL },
        CertificationValidationS.validateCreateEmailCertificationCheckEmailReqBody,
        CertificationApiS.postEmailCertificationCheckEmail,
      );

      if (!this.requested_code) {
        this.requested_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.signup.request_email_verify_code_retry') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.option.color = 'secondary';
        this.component.request_code.redraw();

        this.component.code.option.klass = `${klass.code} ${klass.fade_in}`;
        this.component.code.redraw();

        this.component.email.readonly();
      }

      this.component.code.inputControllerView.restart();

      Toast.open({ label: T('common.send_email_with_certification_code'), close_label: T('common.close') }, {});
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError<ErrorJson>(e)) {
        if (e.response?.data.code === 'SIGNUP_002' && isAxiosError<AuthTypeS.SignupErrorType>(e)) {
          const res = await Modal.open(
            { view: new EmailDuplicatedView({ email: this.component.email.value, error: e.response.data }) },
            {
              header: { title: T('auth.signup.email_duplicated') },
              footer: {
                cancel: T('common.cancel'),
                confirm: T('auth.signup.go_login'),
              },
              backdrop: true,
            },
          );

          if (res === 'confirm') {
            const { client_id } = getSharedData(this);
            location.href = `/${AT.language}/login?email=${this.component.email.value}&client_id=${client_id}`;
          }

          return;
        } else {
          return makeErrorPopup(e, e.response?.data);
        }
      }

      return makeErrorPopup(e);
    }
  }
  async requestCodeVerifyHandler() {
    try {
      if (this.component.code.data.value?.length === 6) {
        await pipe(
          {
            email: this.component.email.value,
            code: this.component.code.data.value,
            certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL,
          },
          CertificationValidationS.validateVerifyEmailCertificationReqBody,
          CertificationApiS.postVerifyEmailCertification,
        );

        this.valid_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.signup.email_verify_code_valid') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.disabled();
        this.component.email.readonly();

        this.component.code.option.klass = `${klass.code} ${klass.fade_out}`;
        this.component.code.inputControllerView.stop();
        this.component.code.redraw();
        this.component.code.element().addEventListener('animationend', () => {
          this.component.code.element().classList.add(klass.disabled);
        });
      }
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        if (e.response?.data.code === 'CERTIFICATION_004') {
          return this.component.code.changeValidatorOnce({
            message: replaceBrToSpace(e.response?.data?.message ?? T('common.errors.title')),
            validator: () => false,
          });
        } else {
          return makeErrorPopup(e, e.response?.data);
        }
      }

      return makeErrorPopup(e);
    }
  }
  async formChangeHandler(e: Event) {
    const form = this.getForm();
    const form_data = fromEntries(new FormData(form).entries());

    if (!this.requested_code) {
      const code_request_res = pipe(
        { ...form_data, certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL },
        CertificationSchemaS.CreateEmailCertificationCheckEmailReqBody.safeParse,
      );

      if (code_request_res.success && this.component.request_code.option.disabled) {
        this.component.request_code.enabled();
      }
      if (!code_request_res.success && !this.component.request_code.option.disabled) {
        this.component.request_code.disabled();
      }
    }

    if (this.requested_code && !this.valid_code) {
      await this.requestCodeVerifyHandler();
    }

    if (this.requested_code && this.valid_code) {
      const submit_res = pipe(
        {
          ...form_data,
          ...this.getAgreeData(),
        },
        AuthSchemaS.SignupControllerReqBody.safeParse,
      );
      const agree_check = this.isCheckAgree(['all_agree', 'is_agree_notification']);
      const nickname_valid = this.component.nickname.isValid();
      const password_valid = this.component.password.isValid();

      if (
        agree_check &&
        submit_res.success &&
        nickname_valid &&
        password_valid &&
        this.component.submit.option.disabled
      ) {
        this.component.submit.enabled();
        return;
      }

      if (
        (!agree_check || !submit_res.success || !nickname_valid || !password_valid) &&
        !this.component.submit.option.disabled
      ) {
        this.component.submit.disabled();
        return;
      }
    }
  }
  async formSubmitHandler(e: Event & { originalEvent: Event }) {
    e.originalEvent.preventDefault();
    const form = this.getForm();
    const { client_id } = getSharedData(this);

    try {
      await pipe(
        {
          ...fromEntries(new FormData(form).entries()),
          ...this.getAgreeData(),
        },
        AuthValidationS.validateSignupReqBody,
        AuthApiS.postSignup,
      );

      return location.replace(makeResultUrl(client_id, { signup: true }));
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e);
    }
  }
}

export class SignupEmailPage extends Page<SignupEmailPageData> {
  protected override template(): Html {
    return html`
      <div>
        ${new Layout(
          { view: new SignupEmailPageBodyView(this.data) },
          {
            fullwidth: true,
            header: this.sharedData?.device === 'mo' ? new Header({}) : undefined,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      history.back();
    });
  }
}
